import InputSelect, { SelectOption } from "../../UI/Inputs/InputSelect";
import { AttributesOption, VehicleConditions, VehicleNames } from "../../../utils/GeneralTypes";
import { Plus, Minus } from "../../Icons";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAllVehicleCategories } from "../../../utils/fetcher";
import { refactorFetchedFilter, refactorSubmittedFilter } from "./utils";
import { useHistory } from "react-router";
import { Controller, useForm } from "react-hook-form";
import LoadingSkeleton from "../../UI/LoadingSkeleton";
import Button from "../../UI/Button";
import style from "./index.module.css";
import { vehicleCategoriesOptions } from "../../../pages/SellVehicle/utils";
import InputField from "../../UI/Inputs/InputField";
import BigNumberInput from "../../UI/Inputs/BigNumberInput";
import { useTranslation } from "../../../hooks/useTranslation";
import { FiltersType } from "./shared";
import { useLocation } from "react-router-dom";

const defaultFilters: FiltersType = {
  brand: "no_option",
  brand_model: "no_option",
  category_0: "no_option",
  // category_1: "no_option",
  category: "no_option",
  min_price: 0,
  max_price: undefined,
  min_year: 1970,
  max_year: new Date().getFullYear() + 1,
  min_mileage: 0,
  max_mileage: undefined,
  attributes: {},
};

const usedDefaultFilters = {
  city: "no_option",
};

type ComponentDataT = {
  brands: SelectOption[];
  // TODO: add types instead of any
  brandsDependants: any[];
  models: SelectOption[];
  colors: SelectOption[];
  categories0: SelectOption[];
  categories0Dependants: any[];
  // categories1: SelectOption[];
  // categories1Dependants: any[];
  categories: SelectOption[];
  cities: SelectOption[];
  attributes: AttributesOption[];
};

const FindVehicleFilter = ({ vehicleNames, vehicleCondition, searchBaseUrl }: ComponentPropsType) => {
  const { search: historySearch } = useLocation();
  const { locale } = useTranslation();
  const history = useHistory();

  const [featuresExpanded, setFeaturesExpanded] = useState<boolean>(false);
  
  const handleDivClick = () => {
    // Only trigger the toggle on smaller screens
    if (window.innerWidth <= 768) { // Adjust the breakpoint as needed
      setFeaturesExpanded((current) => !current);
    }
  };

  const initialMount = useRef<boolean>(true);

  const [componentData, setComponentData] = useState<{
    isFetching: boolean;
    data: ComponentDataT;
  }>({
    isFetching: true,
    data: {
      brands: [],
      brandsDependants: [],
      models: [],
      colors: [],
      categories0: [],
      categories0Dependants: [],
      // categories1: [],
      // categories1Dependants: [],
      categories: [],
      cities: [],
      attributes: [],
    },
  });

  const { register, watch, setValue, handleSubmit, reset, control } = useForm<FiltersType>({
    defaultValues: {
      ...defaultFilters,
      ...(vehicleCondition === "used" ? usedDefaultFilters : {}),
    },
  });

  const searchParams = useMemo(() => {
    return new URLSearchParams(historySearch);
  }, [historySearch]);

  const watchedInputs = watch();
  useEffect(() => {
    if (!initialMount.current) {
      setComponentData((current) => ({
        ...current,
        isFetching: true,
      }));
    }
    initialMount.current = false;
    const data: ComponentDataT = {
      brands: [],
      brandsDependants: [],
      models: [],
      colors: [],
      categories0: [],
      categories0Dependants: [],
      // categories1: [],
      // categories1Dependants: [],
      categories: [],
      cities: [],
      attributes: [],
    };
    (async () => {
      const res = await Promise.all([
        // ...(vehicleNames.single === "truck"
        getAllVehicleCategories(vehicleNames.single),
        // : [
        //     getBrandsFor("new", vehicleNames.single),
        //     getModelsFor(vehicleNames.single),
        //     // getAttributesFor(vehicleNames.single),
        //   ]),
        // ...(vehicleState === "used" ? [getCities()] : []),
      ]);
      // if (vehicleNames.single === "truck") {
      const truckCatsData = vehicleCategoriesOptions(res[0].data.categories);
      data.categories0 = truckCatsData.categories0;
      data.categories0Dependants = truckCatsData.categories0DependantConditions;
      // data.categories1 = truckCatsData.categories1;
      // data.categories1Dependants = truckCatsData.categories1DependantConditions;
      data.brands = truckCatsData.brands;
      data.brandsDependants = truckCatsData.brandsDependantsConditions;
      data.models = truckCatsData.models;
      data.attributes = truckCatsData.attributes;
      // } else {
      //   const brands = moduleToSelectOptions(res[0].data.brands, [["name", ""], "id", "logo"], ["brand_model"]);
      //   data.brands = brands.options;
      //   data.brandsDependants = brands.dependantConditions;
      //   data.models = moduleToSelectOptions(
      //     res[1].data.models,
      //     [["name", ""], "id", "image"],
      //     null,
      //     ["brand"],
      //     false,
      //   ).options;
      //   // data.attributes = refactorAttributes(res[3].data.attributes);
      // }
      // if (vehicleState === "used") {
      //   data.cities = moduleToSelectOptions(res[3].data.cities, [
      //     ["name", ""],
      //     "id",
      //     "",
      //   ]).options;
      // }
      // assign attributes default values
      reset({
        ...defaultFilters,
        ...(vehicleCondition === "used" ? usedDefaultFilters : {}),
        attributes: {
          ...data.attributes.reduce(
            (acc: any, cur: any) => ({
              ...acc,
              // TODO: there's no shape === 5 again use the correct shape name
              [cur.id]: cur.shape === "5" ? false : "no_option",
            }),
            {},
          ),
        },
      });
      if (historySearch) {
        const fetchedFilter = refactorFetchedFilter(historySearch);
        Object.entries(fetchedFilter).forEach((filter) => {
          setValue(filter[0] as keyof FiltersType, filter[1]);
        });
      }

      setComponentData({
        isFetching: false,
        data,
      });
    })();
  }, [vehicleCondition, vehicleNames, reset, setValue, historySearch]);

  const onSubmitFilterHandler = useCallback(
    (filter) => {
      window.scrollTo(0, 0);
      const submittedFilter = refactorSubmittedFilter(filter, {
        ...defaultFilters,
        ...(vehicleCondition === "used" ? usedDefaultFilters : {}),
      });
      // the sorting search params isn't controlled by this component
      // so i don't want to delete it every time the user changes the filter from this component
      // so i will make sure i keep it after changing the filter
      const sortingSearchParams = searchParams.get("sort");
      const nextSearchParams = new URLSearchParams();
      if (sortingSearchParams) {
        nextSearchParams.set("sort", sortingSearchParams);
      }
      Object.entries(submittedFilter).forEach(([key, value]) => {
        nextSearchParams.set(key, value.toString());
      });
      const filterString = nextSearchParams.toString();
      history.push(
        `${searchBaseUrl || `/${vehicleCondition}-${vehicleNames.plural}/find`}${
          filterString ? `?${filterString}` : ""
        }`,
      );
    },
    [vehicleCondition, searchParams, history, searchBaseUrl, vehicleNames.plural],
  );

  return (
    <>
      <div onClick={handleDivClick} className={style.filter_title}>
        <h3 className="main_section__title main_section__title--small">
          {locale("find_a")} {locale(vehicleNames.single)}
        </h3>
        <button>
          {featuresExpanded ? (
            <Minus width="20" height="20" color="#000000" />
          ) : (
            <Plus width="20" height="20" color="#000000" />
          )}
        </button>
      </div>
      <div
        className={`${style.filer_all_sections}${featuresExpanded ? ` ${style["filer_all_sections--expanded"]}` : ""}`}
      >
        {componentData.isFetching ? (
          <div className={style.filter_section}>
            <LoadingSkeleton count={12} />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmitFilterHandler)}>
            {/* {vehicleNames.single === "truck" && ( */}
            <>
              <div className={style.filter_section}>
                <InputSelect
                  props={{
                    ...register("category_0"),
                    value: watchedInputs.category_0,
                  }}
                  selectOptions={componentData.data.categories0}
                  label={locale("truck_classification")}
                  setValue={setValue}
                  watchedInputs={watchedInputs}
                  dependantConditions={componentData.data.categories0Dependants}
                />
              </div>
              {/* <div className={style.filter_section}>
                  <InputSelect
                    props={{
                      ...register("category_1"),
                      value: watchedInputs.category_1,
                    }}
                    selectOptions={componentData.data.categories1}
                    label={locale("truck_category")}
                    setValue={setValue}
                    watchedInputs={watchedInputs}
                    dependantConditions={componentData.data.categories1Dependants}
                  />
                </div> */}
              {/* {componentData.data.categories.filter(
                  (el) =>
                    (el.dependsOn as any[])?.[0]?.value ===
                    watchedInputs.category_1
                ).length > 0 && (
                  <div className={style.filter_section}>
                    <InputSelect
                      props={{
                        ...register("category"),
                        value: watchedInputs.type,
                      }}
                      selectOptions={componentData.data.categories}
                      siteLang={siteLang}
                      label={locale(type}
                      setValue={setValue}
                      watchedInputs={watchedInputs}
                    />
                  </div>
                )} */}
            </>
            {/* )} */}
            <div className={style.filter_section}>
              <InputSelect
                props={{
                  ...register("brand"),
                  value: watchedInputs.brand,
                }}
                selectOptions={[...componentData.data.brands].sort((a, b) => a.label.localeCompare(b.label))}
                label={locale("brand")}
                setValue={setValue}
                watchedInputs={watchedInputs}
                dependantConditions={componentData.data.brandsDependants}
              />
            </div>
            {watchedInputs.category_0 && watchedInputs.category_0 !== "no_option" && (
              <div className={style.filter_section}>
                <InputSelect
                  props={{
                    ...register("brand_model"),
                    value: watchedInputs.brand_model,
                  }}
                  selectOptions={[...componentData.data.models].sort((a, b) => a.label.localeCompare(b.label))}
                  label={locale("model")}
                  watchedInputs={watchedInputs}
                  setValue={setValue}
                />
              </div>
            )}

            <div className={style.price_range_text + " " + style.filter_section}>
              <Controller
                control={control}
                name="min_price"
                rules={{
                  min: 0,
                  max: watchedInputs?.max_price || undefined,
                }}
                render={({
                  field: { onChange, onBlur, value, name, ref },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <BigNumberInput
                    ref={ref}
                    name={name}
                    id="min_price"
                    key="min_price"
                    placeholder={locale("min_price")}
                    value={value}
                    onValueChange={(v) => {
                      onChange(v ? +v : 0);
                    }}
                    label={locale("min_price")}
                    onBlur={onBlur}
                    error={error?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="max_price"
                rules={{
                  min: watchedInputs?.min_price || 0,
                  max: {
                    value: 999_999_999,
                    message: `${locale("max_amount")} = 999,999,999`,
                  },
                }}
                render={({
                  field: { onChange, onBlur, value, name, ref },
                  fieldState: { invalid, isTouched, isDirty, error },
                  formState,
                }) => (
                  <BigNumberInput
                    ref={ref}
                    name={name}
                    id="max_price"
                    key="max_price"
                    value={value}
                    onValueChange={(v) => {
                      if (v && v.length > 9) return;
                      onChange(v ? +v : "");
                    }}
                    onBlur={onBlur}
                    placeholder={locale("max_price")}
                    label={locale("max_price")}
                    error={error?.message}
                  />
                )}
              />
            </div>
            <div className={style.price_range_text + " " + style.filter_section}>
              <InputField
                key="min_year"
                type="number"
                label={locale("min_year")}
                extraProps={{
                  min: 1970,
                  ...register(`min_year`),
                }}
              />

              <InputField
                key="max_year"
                type="number"
                label={locale("max_year")}
                extraProps={{
                  ...register(`max_year`),
                  max: new Date().getFullYear() + 1,
                }}
              />
            </div>

            {/* // TODO: when should we render mileage? */}
            {false && vehicleCondition !== "new" && (
              <div className={style.price_range_text + " " + style.filter_section}>
                <Controller
                  control={control}
                  name="min_mileage"
                  rules={{
                    min: 0,
                    max: watchedInputs?.max_mileage || 9999999.99,
                  }}
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { invalid, isTouched, isDirty, error },
                    formState,
                  }) => (
                    <BigNumberInput
                      ref={ref}
                      name={name}
                      id="min_mileage"
                      key="min_mileage"
                      value={value}
                      onValueChange={(v) => {
                        // to enforce max string length
                        if (v && v?.length > 7) return;
                        onChange(v ? +v : 0);
                      }}
                      onBlur={onBlur}
                      error={error?.message}
                      placeholder={locale("min_mileage")}
                      label={locale("min_mileage")}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="max_mileage"
                  rules={{
                    min: watchedInputs?.min_mileage || 0,
                    max: 9999999.99,
                  }}
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { invalid, isTouched, isDirty, error },
                    formState,
                  }) => (
                    <BigNumberInput
                      ref={ref}
                      name={name}
                      id="max_mileage"
                      key="max_mileage"
                      value={value}
                      onValueChange={(v) => {
                        // to enforce max string length
                        if (v && v?.length > 7) return;
                        onChange(v ? +v : "");
                      }}
                      onBlur={onBlur}
                      error={error?.message}
                      placeholder={locale("max_mileage")}
                      label={locale("max_mileage")}
                    />
                  )}
                />
              </div>
            )}

            {componentData.data.attributes
              .filter((attribute) => attribute.is_filterable && attribute.parentTruck === watchedInputs.category_0)
              .map((attr, index) => (
                <div key={attr.id} className={style.filter_section}>
                  {attr.shape === "select" && (
                    <InputSelect
                      props={{
                        name: `attributes.${attr.id}`,
                        onChange: (e: any) => setValue(e.target.name, e.target.value),
                        value: watchedInputs.attributes[attr.id],
                      }}
                      selectOptions={attr.values.map((val) => ({
                        label: val.name,
                        value: val.value,
                      }))}
                      label={attr.name}
                      watchedInputs={watchedInputs}
                    />
                  )}
                  {/**
                    {attr.shape === "2" && (
                    <InputRadio
                      label={attr.name}
                      props={register(`attributes.${attr.id}`)}
                      radioImage={true}
                      children={attr.values.map((val) => ({
                        name: val.value,
                        value: val.value,
                        image: val.image,
                      }))}
                    />
                  )}
                  {attr.shape === "1" && (
                    <InputRadio
                      label={attr.name}
                      props={register(`attributes.${attr.id}`)}
                      children={attr.values.map((val) => ({
                        name: val.value,
                        value: val.value,
                        image: val.image,
                      }))}
                    />
                  )}
                  */}
                </div>
              ))}
            {componentData.data.attributes.filter((attr) => attr.shape === "checkbox").length > 0 && (
              <div>
                {componentData.data.attributes
                  .filter(
                    (attr) =>
                      attr.shape === "checkbox" && attr.is_filterable && attr.parentTruck === watchedInputs.category_0,
                  )
                  .map((attr) => (
                    <InputField
                      key={attr.id}
                      label={attr.name}
                      type="checkbox"
                      extraProps={register(`attributes.${attr.id}`)}
                    />
                  ))}
              </div>
            )}
            <div className={style.filter_ctas}>
              <Button buttonType="submit" styleType="primary">
                {locale("find")}
              </Button>
              <Button buttonType="button" styleType="primary_black" onClick={() => reset()}>
                {locale("reset")}
              </Button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default memo(
  FindVehicleFilter,
  (prev, next) =>
    prev.vehicleNames.endpoint === next.vehicleNames.endpoint &&
    prev.vehicleNames.plural === next.vehicleNames.plural &&
    prev.vehicleNames.single === next.vehicleNames.single,
);

type ComponentPropsType = {
  vehicleNames: VehicleNames;
  vehicleCondition: VehicleConditions;
  /**
   * sets which link to redirect to after submitting the filter
   *
   * if not set will use `/${vehicleState}-${vehicleNames.plural}/find`
   */
  searchBaseUrl?: string;
};
